/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'
import { useState } from 'react'

import { Modal, ModalHeader, ModalBody, ModalFooter } from '../../../shared/modal'
import { PrimaryButton, SecondaryButton } from '../../../shared/buttons'
import { ButtonType } from '../../../shared/buttons/types'
import { ModalSize } from '../../../shared/modal/Modal/Modal'
import RadioGroup from '../../../shared/input/RadioGroup/RadioGroup'
import { RadioItem } from '../../../shared/input/Radio/Radio'
import useMixpanelEvent from '../../../../lib/hooks/useMixpanelEvent'

type DownloadStudentUsageModalProps = {
  /** Determines visibility of modal */
  isOpen: boolean
  /** Event callback when user triggers a close event (eg. button click or ESC key) */
  onCancel?: () => void
  /** JWT Token required with download form */
  token: string | undefined
}

const defaultProps: Pick<DownloadStudentUsageModalProps, 'onCancel'> = {
  onCancel: undefined
}

const descCss = css`
  padding-top: 10px;
`

const DownloadStudentUsageModal = ({ isOpen, onCancel, token }: DownloadStudentUsageModalProps) => {
  const [downloadUrl, setDownloadUrl] = useState('/reports/users/unique-raw/csv')
  const { addMixpanelEvent } = useMixpanelEvent()
  const downloadOptions: RadioItem[] = [
    {
      description:
        'Use this report to see all unique students and the date they first accessed the Schedule Planner.  Contains Student IDs.',
      label: 'Raw Unique User Data',
      value: '/reports/users/unique-raw/csv'
    },
    {
      description:
        'Use this report to see every time a user has accessed the system.  Contains Student IDs.',
      label: 'Raw Login Data',
      value: '/reports/logins-demand/raw/csv'
    }
  ]

  const handleDownloadTypeChange = (value: string) => setDownloadUrl(value)

  const onDownloadClick = () => {
    addMixpanelEvent('Reports: Download Report for Student Scheduling Usage')
  }

  return (
    <Modal isOpen={isOpen} size={ModalSize.MD}>
      <ModalHeader onClose={onCancel} title="Download Student Usage Data" />
      <ModalBody>
        <div css={descCss}>Select which report you would like to download.</div>
        <RadioGroup
          ariaLabel="Select a report type."
          onChange={handleDownloadTypeChange}
          options={downloadOptions}
          selectedOption={downloadUrl}
          title="Reports"
        />
      </ModalBody>
      <ModalFooter>
        <form method="post" action={process.env.REACT_APP_API_URI + downloadUrl}>
          <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
          <PrimaryButton type={ButtonType.Submit} small onClick={onDownloadClick}>
            Download
          </PrimaryButton>
          <input type="hidden" name="jwt" value={token} />
        </form>
      </ModalFooter>
    </Modal>
  )
}

DownloadStudentUsageModal.defaultProps = defaultProps

export default DownloadStudentUsageModal
